import { create } from "zustand";

const KEY = "airel.mic";

/**
 * Включён ли микрофон — величина глобальная, а не свойство звонка.
 *
 * Так это устроено в том, с чем продукт сравнивают, и так его и ищут:
 * выключатель нажимают заранее, до всякого канала, а войдя — рассчитывают
 * войти уже молча. Пока состояние жило внутри звонка, до входа его не
 * существовало вовсе, и нажать было некуда.
 *
 * Отсюда и второе следствие: выбор переживает перезапуск. Выключивший
 * микрофон вчера не должен обнаружить его включённым сегодня — это как раз
 * тот случай, когда «забыли» замечают последними и не по своей вине.
 *
 * Стором, а не своим списком подписчиков: причина записана в шапке
 * `volume.ts`, и шестой такой список заводить не надо.
 */
interface Microphone {
  /** `true` — говорим, `false` — молчим. */
  on: boolean;
}

function read(): Microphone {
  /*
   * Умолчание — включён: молчащий по умолчанию микрофон человек замечает не
   * сразу, а разговор начинается с «меня слышно?».
   *
   * Хранилища может не оказаться вовсе: модуль тянет за собой звонок, а его
   * поднимают и там, где `localStorage` нет. Падать на этом нельзя — своё
   * умолчание есть.
   */
  try {
    return { on: localStorage.getItem(KEY) !== "0" };
  } catch {
    return { on: true };
  }
}

export const useMicrophone = create<Microphone>()(() => read());

/** Состояние вне отрисовки: звонок спрашивает его в тот миг, когда входит. */
export function micWanted(): boolean {
  return useMicrophone.getState().on;
}

export function setMicWanted(on: boolean) {
  if (useMicrophone.getState().on === on) return;

  useMicrophone.setState({ on });

  try {
    localStorage.setItem(KEY, on ? "1" : "0");
  } catch {
    // Не записалось — переживём: на этот раз выбор проживёт до перезагрузки.
  }
}
